/**
 * /sources/add — connect a NEW source from inside the portal, outside the
 * first-run onboarding wizard.
 *
 * This page owns only the source-type chooser and the done/back plumbing. Each
 * connect flow is the exact same component the onboarding wizard uses
 * (StepConnect for WhatsApp, StepSignalConnect for Signal), so "add a source"
 * and first-run setup stay behaviourally identical and never drift. Each step
 * owns its own sub-flow (QR pairing, chat picking…) and calls back when it
 * finishes.
 *
 * A source is a connected account, never a file: history is imported INTO one
 * from its card on /sources, so an upload can't strand a pile of messages that
 * belongs to nothing.
 */
import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, MessageSquare, NotebookPen, Send, Slack } from 'lucide-react';
import { useState } from 'react';
import { ConnectOption } from '@/components/onboarding/connect-option';
import { StepConnect } from '@/components/onboarding/step-connect';
import { StepGranolaConnect } from '@/components/onboarding/step-granola-connect';
import { StepSignalConnect } from '@/components/onboarding/step-signal-connect';
import { StepSlackConnect } from '@/components/onboarding/step-slack-connect';
import { PageHeader } from '@/components/patterns/page-header';
import { qk, queryClient } from '@/lib/query-client';

type SourceType = 'whatsapp' | 'signal' | 'slack' | 'granola';

export function AddSourcePage() {
  const navigate = useNavigate();
  const [type, setType] = useState<SourceType | null>(null);

  /**
   * A source finished connecting or importing. Refresh every list the new
   * source could surface in, then return to /sources where it now appears.
   */
  const handleDone = () => {
    for (const key of [
      qk.groups,
      qk.wahaSessions,
      qk.signalAccounts,
      qk.slackAccounts,
      qk.granolaAccounts,
      qk.imports,
    ]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
    void navigate({ to: '/sources' });
  };

  return (
    <div className="mx-auto max-w-[640px]">
      <Link
        to="/sources"
        className="inline-flex items-center gap-1 text-[13px] text-faint transition-colors hover:text-foreground"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Back to Sources
      </Link>

      <PageHeader
        className="mt-4"
        title="Add a source"
        subtitle="Connect another WhatsApp, Signal, Slack or Granola account. You can import its history once it's connected."
      />

      <div className="mt-8">
        {type === null ? (
          <div className="space-y-4">
            <ConnectOption
              icon={MessageSquare}
              tone="green"
              title="WhatsApp"
              body="Connect your own number by QR, or use Brain's number in a group. Recent history is backfilled when you connect."
              onClick={() => setType('whatsapp')}
            />
            <ConnectOption
              icon={Send}
              tone="green"
              title="Signal"
              body="Link your own account by QR, or use Brain's number in a group. Once connected, you can import a Signal backup to search older conversations."
              onClick={() => setType('signal')}
            />
            <ConnectOption
              icon={Slack}
              tone="green"
              title="Slack"
              body="Connect your Slack account by OAuth — every channel and DM you're in is indexed automatically."
              onClick={() => setType('slack')}
            />
            <ConnectOption
              icon={NotebookPen}
              tone="green"
              title="Granola"
              body="Paste a personal API key to index your meeting notes and transcripts automatically."
              onClick={() => setType('granola')}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => setType(null)}
              className="text-[13px] text-faint transition-colors hover:text-foreground"
            >
              ← Choose a different source
            </button>

            {type === 'whatsapp' && <StepConnect onNext={handleDone} />}
            {type === 'signal' && <StepSignalConnect onNext={handleDone} />}
            {type === 'slack' && <StepSlackConnect />}
            {type === 'granola' && <StepGranolaConnect onNext={handleDone} />}
          </div>
        )}
      </div>
    </div>
  );
}
